// Shop TypeScript Types

// ============================================================================
// Enums
// ============================================================================

export enum ShopStatus {
  Draft = 'Draft',
  Active = 'Active',
  Suspended = 'Suspended',
  Closed = 'Closed'
}

export enum ProductStatus {
  Draft = 'Draft',
  Active = 'Active',
  Archived = 'Archived',
  OutOfStock = 'OutOfStock'
}

export enum OrderStatus {
  Draft = 'Draft',
  PendingPayment = 'PendingPayment',
  PaymentFailed = 'PaymentFailed',
  Paid = 'Paid',
  PartiallyPaid = 'PartiallyPaid',
  Unpaid = 'Unpaid',
  AwaitingCustomerResponse = 'AwaitingCustomerResponse',
  Confirmed = 'Confirmed',
  Processing = 'Processing',
  ReadyForCollection = 'ReadyForCollection',
  OutForDelivery = 'OutForDelivery',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  Refunded = 'Refunded',
  PartiallyRefunded = 'PartiallyRefunded',
  Abandoned = 'Abandoned',
  // Wholesale Custom WL public-catalog guest checkout — order is placed but
  // parked in this status until the provider accepts (→ Confirmed) or rejects
  // (→ Cancelled) it from the provider order detail screen. No stock is
  // deducted while pending; the customer sees a "pending review" landing
  // page. Serialised by name to match BE OrderStatus.PendingApproval.
  PendingApproval = 'PendingApproval'
}

export enum AvailabilityIssueStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Rejected = 'Rejected',
  Expired = 'Expired'
}

export enum PaymentTransactionType {
  PaymentIntent = 'PaymentIntent',
  PaymentSucceeded = 'PaymentSucceeded',
  PaymentFailed = 'PaymentFailed',
  Refund = 'Refund',
  RefundFailed = 'RefundFailed'
}

export enum PaymentTransactionStatus {
  Pending = 'Pending',
  Processing = 'Processing',
  Succeeded = 'Succeeded',
  Failed = 'Failed',
  Cancelled = 'Cancelled'
}

export enum FulfillmentType {
  Collection = 'Collection',
  Delivery = 'Delivery'
}

export enum UnitOfMeasure {
  Each = 'Each',
  Kilogram = 'Kilogram',
  Gram = 'Gram',
  Litre = 'Litre',
  Millilitre = 'Millilitre',
  Metre = 'Metre',
  Centimetre = 'Centimetre',
  Pack = 'Pack',
  Box = 'Box',
  Dozen = 'Dozen'
}

export enum ProductAttributeType {
  Size = 'Size',
  Color = 'Color',
  Material = 'Material',
  Style = 'Style',
  Custom = 'Custom'
}

export enum ShopImageType {
  Logo = 'Logo',
  Banner = 'Banner',
  Gallery = 'Gallery'
}

export enum ShopPaymentMode {
  ZaveatoManaged = 0,
  ProviderStripe = 1
}

export enum ExtraTargetType {
  SpecificProduct = 0,
  Category = 1,
  AllProducts = 2
}

export enum ExtraPricingType {
  Free = 0,
  FixedPrice = 1,
  PercentageOff = 2
}

// ============================================================================
// Payment Transaction Types
// ============================================================================

export interface PaymentTransaction {
  id: string
  orderId: string
  transactionType: PaymentTransactionType
  transactionTypeDisplay: string
  status: PaymentTransactionStatus
  statusDisplay: string
  attemptNumber: number
  stripePaymentIntentId?: string
  stripeChargeId?: string
  stripeRefundId?: string
  amount: number
  currency: string
  initiatedAt: string
  completedAt?: string
  errorCode?: string
  errorMessage?: string
  parentPaymentId?: string
  refundReason?: string
  // Refund routing — BE fills these on Refund rows only. Method is the enum
  // wire value ('Original' | 'Cash' | ...); MethodDisplay is the friendly
  // label composed on the BE ("Bank transfer" not "BankTransfer") so web +
  // mobile timelines render identical badges. Reference is the operator's
  // optional free-text note (bank ref etc.).
  refundMethod?: 'Original' | 'Cash' | 'Card' | 'BankTransfer' | 'Other'
  refundMethodDisplay?: string
  refundReference?: string
  // Stripe fee details (populated at webhook time from Balance Transaction)
  stripePaymentMethodType?: string
  stripeFeeAmount?: number
  stripeBalanceTransactionId?: string
  paymentMethodDisplay?: string
  isBnpl?: boolean
  // Computed display helpers
  isSuccessful: boolean
  isFailed: boolean
  isRefund: boolean
  isPayment: boolean
}

// ============================================================================
// Shop Types
// ============================================================================

export interface Shop {
  id: string
  providerId: string
  providerUserId?: string
  providerName?: string
  name: string
  shopCode: string
  description?: string
  logoUrl?: string
  bannerUrl?: string
  status: ShopStatus
  currency: string
  /**
   * ISO 3166-1 alpha-2 region code (`GB` / `PK` / `AE` / `SA`). BE-authoritative;
   * seed with `useShopStore().selectShopRegionCode` when you want the fallback-aware
   * value instead of raw undefined.
   */
  regionCode?: string
  /**
   * IANA timezone id (`Europe/London` / `Asia/Karachi` / ...). Consume via
   * `useShopStore().selectShopTimezone` to pick up the `Europe/London` fallback.
   */
  timezone?: string
  offersDelivery: boolean
  offersCollection: boolean
  deliveryFee?: number
  freeDeliveryThreshold?: number
  minimumOrderAmount?: number
  maximumOrderAmount?: number
  orderNumberPrefix?: string
  estimatedDeliveryMinutes?: number
  estimatedCollectionMinutes?: number
  autoPrintReceipts: boolean  // Auto-print receipt on order accept
  autoPrintOnOrderConfirmation: boolean  // Auto-print receipt on order creation
  // Pay on Delivery Settings
  allowPayOnDelivery: boolean
  payOnArrivalDeposit?: number
  availabilityResponseHours: number
  // Cancellation & Refund Settings
  cancellationFee?: number
  excludeDeliveryFeeFromRefund: boolean
  // Dietary Filter Settings
  showDietaryFilters: boolean
  hasDietaryProducts: boolean
  // Wholesaler
  isWholesaler: boolean
  // True once the shop has traded as a wholesaler — the toggle can no longer
  // be switched off. FE renders the switch disabled with an explanatory tooltip.
  isWholesaleLocked?: boolean
  // ShelfLife Tracking
  shelfLifeEnabled: boolean
  // ZavInvoice
  zavInvoiceEnabled: boolean
  autoCarryOver?: boolean
  // SEO
  metaTitle?: string
  metaDescription?: string
  // Invoice Settings
  vatNumber?: string
  bankName?: string
  bankSortCode?: string
  bankAccountNumber?: string
  bankAccountName?: string
  invoicePrefix?: string
  defaultPaymentTerms?: string
  defaultVatRate?: number
  invoiceTermsAndConditions?: string
  // Contact Person
  contactFirstName?: string
  contactLastName?: string
  contactEmail?: string
  contactPhone?: string
  // Address
  addressLine1?: string
  addressLine2?: string
  city?: string
  county?: string
  postcode?: string
  country?: string
  // Geo-location
  latitude?: number
  longitude?: number
  createdAt: string
  updatedAt: string
}

export interface ShopListItem {
  id: string
  providerId: string
  providerName?: string
  name: string
  shopCode: string
  logoUrl?: string
  status: ShopStatus
  productCount: number
  orderCount: number
  createdAt: string
}

export interface ShopStatistics {
  totalProducts: number
  activeProducts: number
  totalVariants: number
  totalOrders: number
  todayOrders: number
  pendingOrders: number
  processingOrders: number
  completedOrders: number
  // Order-outcome pill breakdown (2026-08-07) — drives the "Order
  // Outcomes" pill row on the shop dashboard. Delivered/Collected split
  // Completed by fulfillment type; Refunded groups both fully and
  // partially refunded orders; Cancelled is standalone. Each has a
  // "today"-scoped sibling for the no-date-filter dashboard view.
  deliveredOrders?: number
  todayDeliveredOrders?: number
  collectedOrders?: number
  todayCollectedOrders?: number
  refundedOrders?: number
  todayRefundedOrders?: number
  cancelledOrders?: number
  todayCancelledOrders?: number
  // Total refunded money over the range — pairs with refundedOrders on
  // the Refunded pill so the number reads as "N refunds · £X" rather
  // than a bare count.
  refundedAmount?: number
  todayRefundedAmount?: number
  // Post-ZAV-020/022 fields — see backend ShopStatistics.
  // Committed = accepted orders only; CashReceived = money actually collected.
  committedRevenue: number
  todayCommittedRevenue: number
  thisMonthCommittedRevenue: number
  cashReceived: number
  todayCashReceived: number
  thisMonthCashReceived: number
  // Legacy — retained for backward compat during migration.
  totalRevenue: number
  todayRevenue: number
  thisMonthRevenue: number
  averageOrderValue?: number
  todayAverageOrderValue?: number
}

export interface ShopReadiness {
  isReady: boolean
  hasCategories: boolean
  hasProducts: boolean
  hasAddress: boolean
  hasContactInfo: boolean
  hasFulfillmentOption: boolean
  hasStripeSetup: boolean
  missingRequirements: string[]
}

// ============================================================================
// Public Storefront Types (Customer-facing)
// ============================================================================

export interface PublicShopListItem {
  id: string
  name: string
  shopCode: string
  description?: string
  logoUrl?: string
  bannerUrl?: string
  addressLine1?: string
  city?: string
  postcode?: string
  latitude?: number
  longitude?: number
  allowsCollection: boolean
  allowsDelivery: boolean
  deliveryFee?: number
  freeDeliveryThreshold?: number
  /** ISO 4217 currency code of the shop (BE-authoritative). */
  currency?: string
  estimatedDeliveryMinutes?: number
  estimatedCollectionMinutes?: number
  productCount: number
  categoryCount: number
  // Distance from search location (only set when lat/lng provided)
  distanceKm?: number
  // Opening hours
  isOpen: boolean
  openingHoursText?: string
  // Reviews
  averageRating: number
  reviewCount: number
}

/**
 * Product list item for public API (all products across shops)
 * Includes shop information for display
 */
export interface PublicProductListItem {
  id: string
  name: string
  title: string
  slug?: string
  shortDescription?: string
  // Pricing
  price: number
  compareAtPrice?: number
  isOnSale: boolean
  /** The lowest price among all active variants. If no variants, same as price. */
  lowestVariantPrice: number
  // Category
  categoryId?: string
  categoryName?: string
  // Inventory
  isInStock: boolean
  // Display
  isFeatured: boolean
  // Primary image
  primaryImageUrl?: string
  // Shop information
  shopId: string
  shopName: string
  shopCode: string
  shopLogoUrl?: string
  shopCity?: string
  shopPostcode?: string
  /** ISO 4217 currency code of the owning shop — used by the public
   *  marketplace to format prices per-product (mixed-region listing). */
  shopCurrency?: string
  shopLatitude?: number
  shopLongitude?: number
  shopOffersDelivery: boolean
  shopOffersCollection: boolean
  // Distance from search location (only set when lat/lng provided)
  distanceKm?: number
  // Promotion info
  hasActivePromotion: boolean
  promotionBadgeText?: string
  promotionalPrice?: number
  createdAt: string
}

export interface PublicProductFilters {
  search?: string
  category?: string
  // Comma-separated BaseCategory slugs to EXCLUDE — homepage "Other" chip.
  excludeCategories?: string
  inStock?: boolean
  hasDeals?: boolean
  isFeatured?: boolean
  freeDelivery?: boolean
  isNew?: boolean
  openNow?: boolean
  minPrice?: number
  maxPrice?: number
  // Geo-filtering
  lat?: number
  lng?: number
  radiusKm?: number
  // Fulfillment capability (matches homepage Delivery/Pickup/All toggle)
  delivery?: boolean
  collection?: boolean
  // Pagination
  page?: number
  pageSize?: number
  sortBy?: 'CreatedAt' | 'Price' | 'Name' | 'distance' | 'deals' | 'fastest' | 'deliverytime' | 'Newest'
  sortDirection?: 'asc' | 'desc'
}

export interface PublicShopFilters {
  search?: string
  category?: string  // Category filter (e.g., 'meat', 'grocery')
  // Comma-separated BaseCategory slugs to EXCLUDE — homepage "Other" chip.
  excludeCategories?: string
  delivery?: boolean
  collection?: boolean
  city?: string
  // Geo-filtering (for "shops near me" feature)
  lat?: number
  lng?: number
  radiusKm?: number
  // Quick filters
  freeDelivery?: boolean
  isNew?: boolean
  hasDeals?: boolean
  openNow?: boolean
  maxDeliveryMinutes?: number
  // Pagination
  page?: number
  pageSize?: number
  sortBy?: 'Name' | 'CreatedAt' | 'Newest' | 'DeliveryTime' | 'distance' | 'fastest'
  sortDirection?: 'asc' | 'desc'
}

export interface PaginatedResult<T> {
  items: T[]
  totalCount: number
  totalVariantCount?: number
  page: number
  pageSize: number
  totalPages: number
}

// ============================================================================
// Shop Image Types
// ============================================================================

export interface ShopImage {
  id: string
  shopId: string
  type: ShopImageType
  fileName: string
  url: string
  mimeType: string
  fileSize: number
  altText?: string
  isPrimary: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface UploadShopImageRequest {
  type: ShopImageType
  altText?: string
  setAsPrimary?: boolean
}

export interface CreateShopRequest {
  name: string
  shopCode: string
  // True for the wholesale sibling shop; false for the retail shop.
  // Fixed at creation time (a provider may own 1 of each).
  isWholesaler?: boolean
  description?: string
  logoUrl?: string
  bannerUrl?: string
  currency?: string
  offersDelivery: boolean
  offersCollection: boolean
  deliveryFee?: number
  freeDeliveryThreshold?: number
  minimumOrderAmount?: number
  maximumOrderAmount?: number
  orderNumberPrefix?: string
  estimatedDeliveryMinutes?: number
  estimatedCollectionMinutes?: number
  // Contact Person
  contactFirstName?: string
  contactLastName?: string
  contactEmail?: string
  contactPhone?: string
  // Address
  addressLine1?: string
  addressLine2?: string
  city?: string
  county?: string
  postcode?: string
  country?: string
  // Geo-location
  latitude?: number
  longitude?: number
  // Location
  locationId?: string
}

export interface UpdateShopRequest {
  // General
  name?: string
  shopCode?: string
  description?: string

  // Branding (now handled via image upload, kept for backwards compatibility)
  logoUrl?: string
  bannerUrl?: string

  // Fulfillment
  offersDelivery?: boolean
  offersCollection?: boolean
  deliveryFee?: number
  freeDeliveryThreshold?: number
  deliveryRadiusKm?: number
  collectionInstructions?: string
  estimatedDeliveryMinutes?: number
  estimatedCollectionMinutes?: number

  // Order Settings
  minimumOrderAmount?: number
  maximumOrderAmount?: number
  acceptsPreOrders?: boolean
  orderNumberPrefix?: string
  autoPrintReceipts?: boolean  // Auto-print receipt on order accept
  autoPrintOnOrderConfirmation?: boolean  // Auto-print receipt on order creation

  // Cancellation & Refund Settings
  cancellationFee?: number
  excludeDeliveryFeeFromRefund?: boolean

  // Pay on Delivery Settings
  allowPayOnDelivery?: boolean
  payOnArrivalDeposit?: number
  availabilityResponseHours?: number

  // Contact Person
  contactFirstName?: string
  contactLastName?: string
  contactEmail?: string
  contactPhone?: string

  // Address
  addressLine1?: string
  addressLine2?: string
  city?: string
  county?: string
  postcode?: string
  country?: string

  // Geo-location
  latitude?: number
  longitude?: number

  // Location
  locationId?: string

  // SEO
  metaTitle?: string
  metaDescription?: string

  // Dietary Filter Settings
  showDietaryFilters?: boolean

  // Wholesaler
  isWholesaler?: boolean

  // Wholesale Custom WL public-catalog toggle. Only respected when the
  // shop is a wholesaler — BE returns 400 otherwise.
  showPublicCatalog?: boolean

  // ShelfLife Tracking
  shelfLifeEnabled?: boolean

  // ZavInvoice
  zavInvoiceEnabled?: boolean
  autoCarryOver?: boolean

  // Invoice Settings
  vatNumber?: string
  bankName?: string
  bankSortCode?: string
  bankAccountNumber?: string
  bankAccountName?: string
  invoicePrefix?: string
  defaultPaymentTerms?: string
  defaultVatRate?: number
  invoiceTermsAndConditions?: string
}

// ============================================================================
// Payment Settings Types
// ============================================================================

export interface ShopPaymentSettings {
  paymentMode: ShopPaymentMode
  paymentModeDisplay: string
  isConfigured: boolean
  statusMessage: string
  // Stripe Connect (Zaveato Managed)
  hasStripeConnectAccount: boolean
  stripeConnectAccountId?: string
  stripeConnectOnboardingComplete: boolean
  // Provider's own Stripe (masked)
  hasProviderStripeKeys: boolean
  maskedPublishableKey?: string
  maskedSecretKey?: string
  hasWebhookSecret: boolean
  // Buy Now Pay Later (Klarna, Clearpay) — off by default
  bnplEnabled: boolean
  bnplMinOrderAmount?: number | null
}

export interface UpdatePaymentSettingsRequest {
  paymentMode: ShopPaymentMode
  stripePublishableKey?: string | null
  stripeSecretKey?: string | null
  stripeWebhookSecret?: string | null
  bnplEnabled: boolean
  bnplMinOrderAmount?: number | null
}

export interface ValidateStripeKeysRequest {
  publishableKey: string
  secretKey: string
}

export interface ValidateStripeKeysResult {
  isValid: boolean
  errorMessage?: string
  accountName?: string
  accountId?: string
}

// ============================================================================
// Product Types
// ============================================================================

export interface Product {
  id: string
  shopId: string
  categoryId?: string
  categoryName?: string
  name: string
  title: string
  slug: string
  description?: string
  shortDescription?: string
  sku?: string
  barcode?: string
  price: number
  compareAtPrice?: number
  costPrice?: number
  /** The lowest price among all active variants. If no variants, same as price. */
  lowestVariantPrice?: number
  /** ISO 4217 currency snapshot from the owning Shop.Currency. Populated by
   *  the BE on every product list DTO so a mixed-region marketplace can
   *  render "PKR 2,000" for PK shops without a per-view currency lookup. */
  shopCurrency?: string
  status: ProductStatus
  isFeatured: boolean
  isExtra: boolean
  payableOnArrival: boolean
  // Dietary Information
  isVegan: boolean
  isVegetarian: boolean
  isHalal: boolean
  hasVariants: boolean
  trackInventory: boolean
  stockQuantity: number
  lowStockThreshold: number
  allowBackorder: boolean
  unitOfMeasure: UnitOfMeasure
  weight?: number
  weightUnit?: string
  metaTitle?: string
  metaDescription?: string
  sortOrder: number
  variants: ProductVariant[]
  images: ProductImage[]
  attributes: ProductAttribute[]
  optionGroups?: ProductOptionGroup[]
  /** Convenience property for primary image URL (may not be present in all API responses) */
  primaryImageUrl?: string
  createdAt: string
  updatedAt: string
}

export interface ProductListItem {
  id: string
  shopId: string
  categoryId?: string
  categoryName?: string
  categoryFullPath?: string
  name: string
  title: string
  slug: string
  sku?: string
  price: number
  compareAtPrice?: number
  /** The lowest price among all active variants. If no variants, same as price. */
  lowestVariantPrice: number
  status: ProductStatus
  isFeatured: boolean
  isExtra: boolean
  payableOnArrival: boolean
  // Dietary Information
  isVegan: boolean
  isVegetarian: boolean
  isHalal: boolean
  variantCount: number
  hasVariants: boolean
  trackInventory: boolean
  stockQuantity?: number | null
  lowStockThreshold?: number
  isInStock: boolean
  isLowStock: boolean
  unitOfMeasure?: string
  unitOfMeasureDisplay?: string
  primaryImageUrl?: string
  sortOrder: number
  createdAt: string
}

export interface ProductVariant {
  id: string
  productId: string
  name: string
  sku?: string
  barcode?: string
  price: number
  compareAtPrice?: number
  costPrice?: number
  // Inventory
  trackInventory: boolean
  stockQuantity?: number
  allowBackorder: boolean
  // Other
  isActive: boolean
  payableOnArrival: boolean
  weightKg?: number
  imageUrl?: string
  sortOrder: number
  attributes: ProductAttribute[]
}

export interface ProductOptionGroup {
  id: string
  productId: string
  name: string
  displayName?: string
  isRequired: boolean
  allowMultiple: boolean
  minSelections?: number
  maxSelections?: number
  sortOrder: number
  isActive: boolean
  choices: ProductOptionChoice[]
}

export interface ProductOptionChoice {
  id: string
  optionGroupId: string
  name: string
  description?: string
  priceAdjustment: number
  isDefault: boolean
  sortOrder: number
  isActive: boolean
}

export interface CartItemOptionSelection {
  optionGroupId: string
  optionChoiceId: string
}

// ────────────────────────────────────────────────────────────────────────
// Provider ProductPickerDialog types
// ────────────────────────────────────────────────────────────────────────
// Emitted by <ProductPickerDialog> when the operator confirms a pick
// during the availability/amend flow. Types live here (rather than inside
// the component's <script setup>) so parent views can strongly-type the
// @picked handler without depending on the Vue SFC's compiled export.

/** A single validated option-choice selection, plus its display + delta
 *  metadata. Sent to the BE (id fields only) for re-validation + price
 *  folding + audit persistence, and used FE-side to compute the live line
 *  total. */
export interface PickedOptionSelection {
  optionGroupId: string
  optionChoiceId: string
  groupName: string
  choiceName: string
  priceAdjustment: number
}

/** A single extra the operator chose alongside the main product. Parent
 *  view converts each entry into its own new-line entry with isExtra=true
 *  + productExtraId set so the BE can look up the ProductExtra config and
 *  derive the correct price from ProductExtra.CalculatePrice. */
export interface PickedExtra {
  /** ProductExtra.id — the config row driving pricing (Free/Fixed/%Off). */
  productExtraId: string
  /** The extra product itself — becomes a new OrderItem stamped IsExtra=true. */
  productId: string
  name: string
  /** Snapshot of the price shown to the operator (BE re-derives at persist). */
  unitPrice: number
  quantity: number
  imageUrl?: string
}

/** Full payload emitted by the picker on confirm. */
export interface PickerPayload {
  productId: string
  productVariantId?: string
  quantity: number
  /** Unit price including option adjustments — for FE preview arithmetic.
   *  BE always re-derives from productId + variantId + optionSelections. */
  unitPrice: number
  /** Composed display label (e.g. "Chicken - 1kg"). */
  displayName: string
  imageUrl?: string
  optionSelections: PickedOptionSelection[]
  extras: PickedExtra[]
  notes?: string
}

/**
 * Per-line action mode captured in the Contact Customer / Amend Order dialog.
 * Mutually exclusive:
 *   - 'unavailable' — remove the line (0 qty).
 *   - 'reduced'     — partial-fulfilment; caller sets availableQuantity.
 *   - 'substitute'  — swap the line for an alternate product picked in the
 *                     ProductPickerDialog.
 * Absence from the selection map = "no change".
 */
export type AvailabilityMode = 'unavailable' | 'reduced' | 'substitute'

/**
 * Draft per-line change captured in the Contact Customer / Amend Order
 * dialog. `mode` gates which of the alternative* / availableQuantity fields
 * are meaningful. BE re-validates and re-derives prices from the id fields
 * on accept — the FE-side numeric fields are only used for the live delta
 * preview.
 */
export interface AvailabilityEntry {
  mode: AvailabilityMode
  availableQuantity?: number
  alternativeProductId?: string
  alternativeVariantId?: string
  alternativeProductLabel?: string
  alternativeUnitPrice?: number
  alternativeQuantity?: number
  alternativeImageUrl?: string
  alternativeOptionSelections?: PickedOptionSelection[]
  /** "Cut: Curry, Size: Large" — human-readable summary of the option picks. */
  alternativeOptionSummary?: string
}

/**
 * A new order line added mid-flow by the provider (Contact Customer / Amend
 * Order). Extras carry `productExtraId` + `isExtra` so the BE uses
 * ProductExtra.CalculatePrice for the unit price and stamps IsExtra=true on
 * the resulting OrderItem.
 */
export interface NewLineEntry {
  productId: string
  variantId?: string
  label: string
  unitPrice: number
  quantity: number
  optionSelections?: PickedOptionSelection[]
  optionSummary?: string
  isExtra?: boolean
  productExtraId?: string
  imageUrl?: string
}

export interface OrderItemOptionSelectionDto {
  optionGroupId?: string
  optionChoiceId?: string
  groupName: string
  choiceName: string
  priceAdjustment: number
}

export interface ProductImage {
  id: string
  productId: string
  imageUrl: string
  altText?: string
  isPrimary: boolean
  sortOrder: number
}

export interface ProductAttribute {
  id: string
  name: string
  value: string
  attributeType: ProductAttributeType
}

export interface CreateProductRequest {
  categoryId?: string
  name: string
  title: string
  description?: string
  shortDescription?: string
  sku?: string
  barcode?: string
  price: number
  compareAtPrice?: number
  costPrice?: number
  isFeatured?: boolean
  isExtra?: boolean
  payableOnArrival?: boolean
  isVegan?: boolean
  isVegetarian?: boolean
  isHalal?: boolean
  trackInventory?: boolean
  stockQuantity?: number
  lowStockThreshold?: number
  allowBackorder?: boolean
  maxQuantityPerOrder?: number
  unitOfMeasure?: UnitOfMeasure
  weight?: number
  weightUnit?: string
  metaTitle?: string
  metaDescription?: string
  imageUrls?: string[]
}

export interface UpdateProductRequest {
  categoryId?: string
  name?: string
  title?: string
  description?: string
  shortDescription?: string
  sku?: string
  barcode?: string
  price?: number
  compareAtPrice?: number
  costPrice?: number
  status?: ProductStatus
  isFeatured?: boolean
  isExtra?: boolean
  payableOnArrival?: boolean
  isVegan?: boolean
  isVegetarian?: boolean
  isHalal?: boolean
  trackInventory?: boolean
  stockQuantity?: number
  lowStockThreshold?: number
  allowBackorder?: boolean
  maxQuantityPerOrder?: number
  unitOfMeasure?: UnitOfMeasure
  weight?: number
  weightUnit?: string
  metaTitle?: string
  metaDescription?: string
}

export interface UpdateInventoryRequest {
  stockQuantity: number
  lowStockThreshold?: number
}

export interface CreateProductVariantRequest {
  name: string
  sku?: string
  barcode?: string
  price: number
  compareAtPrice?: number
  costPrice?: number
  // Inventory
  trackInventory?: boolean
  stockQuantity?: number
  allowBackorder?: boolean
  // Other
  isActive?: boolean
  payableOnArrival?: boolean
  weightKg?: number
  imageUrl?: string
  attributes?: CreateAttributeRequest[]
}

export interface UpdateProductVariantRequest {
  name: string
  sku?: string
  barcode?: string
  price: number
  compareAtPrice?: number
  costPrice?: number
  // Inventory
  trackInventory?: boolean
  stockQuantity?: number
  allowBackorder?: boolean
  // Other
  isActive?: boolean
  payableOnArrival?: boolean
  weightKg?: number
  imageUrl?: string
}

export interface CreateAttributeRequest {
  name: string
  value: string
  attributeType: ProductAttributeType
}

export interface AddProductImageRequest {
  imageUrl: string
  altText?: string
  isPrimary?: boolean
}

export interface ReorderImagesRequest {
  imageIds: string[]
}

// ============================================================================
// Product Extra Types
// ============================================================================

export interface ProductExtra {
  id: string
  shopId: string
  extraProductId: string
  extraProductName: string
  extraProductImageUrl?: string
  extraProductBasePrice: number
  targetType: ExtraTargetType
  targetTypeDisplay: string
  targetProductId?: string
  targetProductName?: string
  targetCategoryId?: string
  targetCategoryName?: string
  pricingType: ExtraPricingType
  pricingTypeDisplay: string
  extraPrice?: number
  discountPercent?: number
  calculatedPrice: number
  minQuantity?: number
  minOrderValue?: number
  displayText?: string
  badgeText?: string
  displayOrder: number
  isActive: boolean
  startsAt?: string
  endsAt?: string
  isCurrentlyValid: boolean
  payableOnArrival: boolean
  createdAt: string
  updatedAt?: string
}

export interface ProductExtraListItem {
  id: string
  extraProductId: string
  extraProductName: string
  extraProductImageUrl?: string
  targetType: ExtraTargetType
  targetTypeDisplay: string
  targetName?: string
  pricingType: ExtraPricingType
  pricingDisplay: string
  isActive: boolean
  isCurrentlyValid: boolean
  displayOrder: number
  payableOnArrival: boolean
}

export interface AvailableExtra {
  extraId: string
  extraProductId: string
  name: string
  description?: string
  imageUrl?: string
  price: number
  badgeText: string
  displayText?: string
  isFree: boolean
  payableOnArrival: boolean
}

/**
 * Attractive extra for promotional display on storefront.
 * Shows free or cheap extras from all active shops.
 */
export interface AttractiveExtra {
  extraId: string
  extraProductId: string
  name: string
  description?: string
  imageUrl?: string
  originalPrice: number
  finalPrice: number
  badgeText: string
  displayText?: string
  isFree: boolean
  // Shop info for context
  shopId: string
  shopCode: string
  shopName: string
  shopLogoUrl?: string
  // Target info for navigation (API returns string values, not numeric enum)
  targetType: 'AllProducts' | 'SpecificProduct' | 'Category'
  targetProductId?: string
  targetProductSlug?: string
  targetProductName?: string
  targetCategoryId?: string
  targetCategoryName?: string
}

export interface ExtraProduct {
  id: string
  name: string
  description?: string
  imageUrl?: string
  price: number
  sku: string
  isActive: boolean
  categoryName?: string
  payableOnArrival: boolean
}

export interface CreateProductExtraRequest {
  extraProductId: string
  targetType: ExtraTargetType
  targetProductId?: string
  targetCategoryId?: string
  pricingType: ExtraPricingType
  extraPrice?: number
  discountPercent?: number
  minQuantity?: number
  minOrderValue?: number
  displayText?: string
  badgeText?: string
  displayOrder?: number
  startsAt?: string
  endsAt?: string
  activate?: boolean
  payableOnArrival?: boolean
}

export interface UpdateProductExtraRequest {
  targetType: ExtraTargetType
  targetProductId?: string
  targetCategoryId?: string
  pricingType: ExtraPricingType
  extraPrice?: number
  discountPercent?: number
  minQuantity?: number
  minOrderValue?: number
  displayText?: string
  badgeText?: string
  displayOrder?: number
  startsAt?: string
  endsAt?: string
  payableOnArrival?: boolean
}

export interface CartExtrasRequest {
  items: CartExtrasItem[]
  orderTotal: number
}

export interface CartExtrasItem {
  productId: string
  quantity: number
}

// ============================================================================
// Category Types
// ============================================================================

export interface ProductCategory {
  id: string
  shopId: string
  name: string
  slug?: string
  description?: string
  imageUrl?: string
  parentCategoryId?: string
  parentCategoryName?: string
  baseCategoryId?: string
  baseCategoryName?: string
  /** Full path from root to this category (e.g., "Electronics > Phones > Accessories") */
  fullPath: string
  type?: string
  typeDisplay?: string
  /** Depth level: 1 = root, 2 = child, 3 = grandchild (max) */
  depth: number
  /** Whether this category can have children (only depth 1 and 2) */
  canHaveChildren: boolean
  isActive: boolean
  sortOrder: number
  productCount: number
  subCategories: ProductCategory[]
  createdAt?: string
  updatedAt?: string
}

export interface CreateProductCategoryRequest {
  name: string
  description?: string
  imageUrl?: string
  parentCategoryId?: string
  baseCategoryId?: string
  sortOrder?: number
  isActive?: boolean
}

export interface UpdateProductCategoryRequest {
  name: string
  description?: string
  imageUrl?: string
  parentCategoryId?: string
  baseCategoryId?: string
  sortOrder?: number
  isActive?: boolean
}

export interface CategoryOrderItem {
  categoryId: string
  sortOrder: number
  parentCategoryId?: string
}

export interface ReorderCategoriesRequest {
  orderItems: CategoryOrderItem[]
}

// ============================================================================
// Order Audit Types
// ============================================================================

export interface OrderAuditEntry {
  id: string
  action: string
  actionDisplay: string
  description: string
  userId?: string
  userEmail?: string
  userName?: string
  timestamp: string
  changes?: string
}

// ============================================================================
// Order Types
// ============================================================================

/**
 * Where the order originated. Drives the "channel" badge/filter on order
 * lists and the "Revenue by Channel" chart on analytics dashboards.
 * Matches the backend Zaveato.Domain.Enums.OrderSource enum (by string name).
 */
export enum OrderSource {
  ZaveatoWebsite = 'ZaveatoWebsite',
  ZaveatoMobileApp = 'ZaveatoMobileApp',
  WhiteLabel = 'WhiteLabel',
}

export interface Order {
  id: string
  shopId: string
  shopName?: string
  customerId?: string
  orderNumber: string
  status: OrderStatus
  statusDisplay?: string
  fulfillmentType: FulfillmentType
  fulfillmentTypeDisplay?: string
  /** Channel — Website / Mobile App / White Label. */
  source?: OrderSource
  /** Friendly label of `source` (e.g. "White Label") populated by the API. */
  sourceDisplay?: string
  /** Specific WL custom-domain hostname when source = WhiteLabel; null otherwise. */
  originHostname?: string
  customerName: string
  customerEmail: string
  customerPhone?: string
  /**
   * Wholesale business fields — populated on Wholesale Custom WL public-catalog
   * (PendingApproval) orders and on InvoiceLater orders where the buyer supplied
   * a company name / VAT / PO ref at checkout. Null on retail orders.
   */
  customerCompanyName?: string
  customerVatNumber?: string
  customerPurchaseOrderReference?: string
  deliveryAddressLine1?: string
  deliveryAddressLine2?: string
  deliveryCity?: string
  /** Sub-national area — UK "County", PK "Province", AE "Emirate", SA "Region".
   *  Populated when the customer supplied it at checkout (PK/AE/SA show the
   *  input by default; GB hides it). BE-authoritative snapshot on the order. */
  deliveryProvince?: string
  deliveryPostcode?: string
  deliveryCountry?: string
  deliveryInstructions?: string
  dropOffOption?: string
  dropOffOptionDisplay?: string
  formattedDeliveryAddress?: string
  subtotal: number
  deliveryFee: number
  discount: number
  discountAmount: number
  discountCode?: string
  discountCodeId?: string
  tax: number
  taxAmount: number
  taxRate?: number
  totalAmount: number
  refundedAmount: number
  refundedAt?: string
  isFullyRefunded: boolean
  isPartiallyRefunded: boolean
  netPaidAmount: number
  currency: string
  /**
   * Money the customer has actually paid to date, sourced from
   * `Order.AmountPaidToDate` on the BE. Populated by
   * ZaveatoShopMappingProfile.AfterMap. Both surfaces (this + mobile)
   * read this instead of summing `paymentHistory` client-side so a stale
   * or partial ledger can't drift the Summary block off from the
   * refund cap the server enforces.
   */
  amountPaidToDate?: number
  /**
   * Money the shop kept — BE-computed as `max(0, amountPaidToDate −
   * refundedAmount)`. Single source of truth for the "Net Retained"
   * (provider/admin) / "Net Balance" (customer) row on the Summary
   * block. Never recompute client-side.
   */
  netRetainedAmount?: number
  // Payment summary
  paidAt?: string
  isPaid: boolean
  paymentAttemptCount: number
  lastPaymentError?: string
  // Payment history (from ShopOrderPayments)
  paymentHistory: PaymentTransaction[]
  // Fulfillment timeline
  requestedCollectionTime?: string
  requestedDeliveryTime?: string
  confirmedAt?: string
  processingStartedAt?: string
  readyAt?: string
  completedAt?: string
  cancelledAt?: string
  // Notes
  customerNotes?: string
  internalNotes?: string
  cancellationReason?: string
  // Pay on Delivery
  paymentMethod: 'Online' | 'PayOnDelivery' | 'InvoiceLater'
  paymentMethodDisplay: string
  paymentStatus: 'Pending' | 'PartiallyPaid' | 'Paid'
  paymentStatusDisplay: string
  podPaymentMethod?: 'Cash' | 'Card' | 'BankTransfer'
  podPaymentMethodDisplay?: string
  depositAmount?: number
  amountDue: number
  paymentCollectedAt?: string
  canCollectPayment: boolean
  // Computed
  itemCount: number
  uniqueItemCount: number
  canBeCancelled: boolean
  canBeRefunded: boolean
  /**
   * Server-computed refund cap = max(0, amountPaidToDate - refundedAmount).
   * Both web + mobile render this value on the Process Refund dialog and
   * use it to gate the input. Same figure the server enforces on POST
   * /refund, so what the dialog says and what the server accepts are
   * guaranteed to agree.
   */
  maxRefundableAmount?: number
  /**
   * Remaining amount that can still be pushed back through Stripe. Mirrors
   * Order.RemainingStripeCapacity on the BE — same math the refund guard
   * enforces on submit. Drives the refund-method picker (Original hidden
   * when 0, manual methods hidden when this fully covers the current
   * refund amount). SoT — never recomputed on the FE from the payments
   * array.
   */
  remainingStripeCapacity?: number
  /**
   * True when the order has at least one succeeded non-refund payment
   * that did NOT flow through Stripe (POD cash, in-person card, bank
   * transfer, legacy). Descriptive only — the filter keys off
   * `remainingStripeCapacity` vs the current refund amount.
   */
  hasNonStripePayments?: boolean
  canRaiseAvailabilityIssue: boolean
  /**
   * Shop-level cancellation fee snapshot (ZAV-025). Mirrors
   * Shop.CancellationFee at read time and drives the fee breakdown block
   * on the customer order detail view. 0 (or missing) hides the block.
   */
  cancellationFee?: number
  /**
   * Whether the shop excludes delivery fee from refunds. Sourced from
   * Shop.ExcludeDeliveryFeeFromRefund so cancel dialogs can render the
   * "delivery fee not refundable" note without a second fetch.
   */
  excludeDeliveryFeeFromRefund?: boolean
  /**
   * BE-relaxed cancel guard. Unlike `canBeCancelled` (order-status only),
   * `canCancel` stays true even while an availability issue is pending
   * customer response — cancelling the order also voids the pending
   * request. Populated by BE alongside the amendment-history rewrite.
   */
  canCancel?: boolean
  /**
   * True when the order currently has a Pending OrderAvailabilityIssue
   * awaiting customer response. Drives the "This will also cancel the
   * pending customer request" warning in the cancel dialog.
   */
  hasPendingAvailabilityIssue?: boolean
  /**
   * Outstanding balance the customer still owes after mid-flow order
   * changes (e.g. amendment added an item). BE-computed so we don't have
   * to redo the arithmetic client-side. Populated on both POD and
   * previously-paid Online orders where the total went up.
   */
  extraToCollectAmount?: number
  /**
   * True when the BE will accept a POST /collect-extra-payment for this
   * order (extra balance > 0, provider-eligible, order not cancelled).
   */
  canCollectExtraPayment?: boolean
  // Items
  items: OrderItem[]
  /**
   * All historical availability issues on this order — accepted,
   * declined, expired, and pending. Populated by BE for the persistent
   * "Amendment History" section on order detail.
   */
  availabilityIssues?: OrderAvailabilityIssue[]
  /**
   * Full lifecycle event stream — Created, Paid, AwaitingCustomerResponse,
   * AmendmentAccepted, AmendmentDeclined, ExtraPaymentCollected,
   * Processing, Ready, Completed, Cancelled, Refunded, etc. Populated
   * by BE; when present, drives the Order Timeline. Older orders that
   * pre-date this field fall back to the legacy timestamp mapping.
   */
  timeline?: OrderTimelineEvent[]
  createdAt: string
  updatedAt: string
}

/**
 * One event in the order's lifecycle. `stage` groups events for
 * colour-coding: 'lifecycle' (blue), 'awaiting' (amber), 'accepted'
 * (green), 'declined' (red).
 */
export interface OrderTimelineEvent {
  /**
   * Machine-friendly stage — matches BE OrderTimelineEventDto.Stage.
   * One of: Created, Paid, Confirmed, Processing, Ready,
   * AwaitingCustomerResponse, AmendmentAccepted, AmendmentDeclined,
   * ExtraPaymentCollected, Completed, Cancelled, Refunded.
   */
  stage: string
  /** UTC ISO timestamp (BE ships as `timestamp`). */
  timestamp: string
  /** Optional freeform description — e.g. reason for cancel. */
  description?: string
  /** Email of who triggered it — BE ships as `actorEmail`. */
  actorEmail?: string
}

export interface OrderListItem {
  id: string
  shopId: string
  orderNumber: string
  status: OrderStatus
  /**
   * BE-composed operational label for the status pill — populated by
   * `OrderDisplayHelpers.ForStatus` on the server so web + mobile show
   * byte-identical wording. Optional for backwards compat with cached
   * responses that pre-date this field; FE keeps a local fallback.
   */
  statusDisplay?: string
  fulfillmentType: FulfillmentType
  /** Channel — Website / Mobile App / White Label. */
  source?: OrderSource
  /** Friendly label of `source` populated by the API. */
  sourceDisplay?: string
  /** Specific WL custom-domain hostname when source = WhiteLabel. */
  originHostname?: string
  customerName: string
  customerEmail: string
  customerNotes?: string
  deliveryInstructions?: string
  dropOffOptionDisplay?: string
  itemCount: number
  totalAmount: number
  currency: string
  createdAt: string
  paidAt?: string
  // Pay on Delivery
  paymentMethod: 'Online' | 'PayOnDelivery' | 'InvoiceLater'
  paymentMethodDisplay: string
  paymentStatus: 'Pending' | 'PartiallyPaid' | 'Paid'
  paymentStatusDisplay: string
  podPaymentMethod?: 'Cash' | 'Card' | 'BankTransfer'
  podPaymentMethodDisplay?: string
  amountDue: number
  canCollectPayment: boolean
  /** ZavInvoice linked when the wholesale (InvoiceLater) order has been
   *  billed. Null until the provider generates the invoice. */
  zavInvoiceId?: string | null
  // Refund info
  refundedAmount: number
  hasRefund: boolean
  /**
   * Customer paid-to-date on this order. BE-computed; mirrors
   * `Order.AmountPaidToDate`. Used together with `netRetainedAmount` to
   * decide whether the muted "Net £X" line renders under the total on the
   * list row (only when there's a refund OR a partial payment).
   */
  amountPaidToDate?: number
  /**
   * Money the shop kept — BE-computed as `max(0, amountPaidToDate −
   * refundedAmount)`. Single source of truth for the muted "Net £X" line
   * shown under the total on the shop All Orders list, Live Orders
   * dashboard, admin orders list and mobile equivalents. Replaces the
   * misleading raw `-£X refunded` line that invited mental subtraction
   * from an already-amendment-reduced total. FE never recomputes.
   */
  netRetainedAmount?: number
  /**
   * Mirrors `Order.CanRaiseAvailabilityIssue()` — true when the order sits
   * in an actionable pre-fulfilment status (Unpaid / PartiallyPaid / Paid
   * / Confirmed / Processing) AND has no pending availability issue in
   * flight. Payment-method agnostic (works for POD, Online-Paid,
   * InvoiceLater). Gates the Contact Customer button on the orders list +
   * live dashboard rows without needing a full-order fetch.
   */
  canRaiseAvailabilityIssue: boolean
}

export interface OrderItem {
  id: string
  orderId: string
  productId: string
  productVariantId?: string
  productName: string
  productSku?: string
  variantName?: string
  productImageUrl?: string
  unitPrice: number
  quantity: number
  totalPrice: number
  discountAmount?: number
  unitOfMeasure?: string
  unitOfMeasureDisplay?: string
  unitDisplay?: string
  quantityDisplay?: string
  notes?: string
  // Dietary Information
  isVegan?: boolean
  isVegetarian?: boolean
  isHalal?: boolean
  isExtra?: boolean
  // Availability tracking
  isUnavailable?: boolean
  originalQuantity?: number
  /**
   * BE-derived: whether this item's product (or variant, if variant-scoped)
   * has inventory tracking enabled. Drives the restock UI in the refund
   * modal — only true lines get the checkbox / per-line quantity picker.
   * Do NOT compute client-side; the backend uses variant-first-else-product
   * for the source-of-truth flag.
   */
  tracksInventory?: boolean
  /**
   * Line-level lifecycle status. 'Requested' by default; flips to
   * ModifiedQuantity / Substituted / Removed via the availability accept
   * flow, or Refunded when a per-line refund is issued.
   */
  status?: OrderItemStatus
  // Product option selections
  optionSelections?: OrderItemOptionSelectionDto[]
  /**
   * When status === 'Substituted', carries the pre-substitution snapshot
   * (original product name + qty + unit price) so the provider grid can
   * render "Originally ordered: N × X (£Y each)" beneath the substituted
   * line. Null on non-substituted lines and on legacy substituted rows
   * that pre-date the snapshot column.
   */
  substitutionContext?: OrderItemSubstitutionContext
}

export interface OrderItemSubstitutionContext {
  originalProductName: string
  originalVariantName?: string
  originalQuantity: number
  originalUnitPrice: number
}

export type OrderItemStatus =
  | 'Requested'
  | 'ModifiedQuantity'
  | 'Substituted'
  | 'Removed'
  | 'Refunded'

export type DropOffOption = 'HandToMe' | 'MeetAtDoor' | 'LeaveAtDoor'

export interface CreateOrderRequest {
  shopId: string
  customerId?: string
  fulfillmentType: FulfillmentType
  customerName: string
  customerEmail: string
  customerPhone?: string
  deliveryAddressLine1?: string
  deliveryAddressLine2?: string
  deliveryCity?: string
  deliveryPostcode?: string
  deliveryCountry?: string
  deliveryInstructions?: string
  dropOffOption?: DropOffOption
  customerNotes?: string
  items: CreateOrderItemRequest[]
}

export interface CreateOrderItemRequest {
  productId: string
  productVariantId?: string
  quantity: number
}

export interface UpdateOrderStatusRequest {
  status: OrderStatus
  notes?: string
}

export interface OrderStatistics {
  totalOrders: number
  pendingOrders: number
  completedOrders: number
  cancelledOrders: number
  totalRevenue: number
  averageOrderValue: number
  ordersByStatus: Record<OrderStatus, number>
  ordersByFulfillment: Record<FulfillmentType, number>
}

// ============================================================================
// Payment Types
// ============================================================================

export interface PaymentIntentResult {
  success: boolean
  clientSecret?: string
  paymentIntentId?: string
  amount?: number
  currency?: string
  errorMessage?: string
}

export interface RefundResult {
  success: boolean
  refundId?: string
  amount?: number
  errorMessage?: string
}

export interface CancelOrderResult {
  order: Order
  refundRequested: boolean
  refundSuccessful: boolean
  refundAmount?: number
  refundId?: string
  refundError?: string
  refundAttempts: number
}

// ============================================================================
// Order Availability Issue Types (POD Orders)
// ============================================================================

export interface OrderAvailabilityIssue {
  id: string
  orderId: string
  orderNumber: string
  status: AvailabilityIssueStatus
  statusDisplay: string
  expiresAt: string
  timeRemaining?: string
  operatorMessage?: string
  createdByUserId: string
  createdByUserName: string
  originalOrderStatus: OrderStatus
  respondedAt?: string
  originalOrderTotal: number
  proposedOrderTotal: number
  difference: number
  items: AvailabilityIssueItem[]
  /**
   * Pending NEW lines the provider added mid-flow — mirrors the customer-
   * facing `AvailabilityIssuePublic.newLines`. Rendered under a "Being added"
   * section in the provider pending-changes banner.
   */
  newLines?: AvailabilityIssueNewLine[]
  createdAt: string
}

export interface AvailabilityIssueNewLine {
  productName: string
  variantName?: string
  productImageUrl?: string
  quantity: number
  unitPrice: number
  lineTotal: number
  notes?: string
  isExtra?: boolean
}

export interface AvailabilityIssueItem {
  id: string
  orderItemId: string
  productName: string
  variantName?: string
  productImageUrl?: string
  originalQuantity: number
  originalUnitPrice: number
  originalLineTotal: number
  availableQuantity?: number
  isUnavailable: boolean
  newLineTotal: number
  priceImpact: number
  alternativeProductId?: string
  alternativeVariantId?: string
  alternativeProductName?: string
  alternativePrice?: number
  /** Alternate quantity — was already on the backend DTO but missing from
   *  the FE type; needed for rendering "3 × Ribeye" on the pending-issue
   *  banner. */
  alternativeQuantity?: number
  alternativeNote?: string
  hasAlternative: boolean
}

export interface OrderAvailabilityIssueSummary {
  id: string
  status: AvailabilityIssueStatus
  statusDisplay: string
  expiresAt: string
  timeRemaining?: string
  affectedItemCount: number
  originalTotal: number
  proposedTotal: number
  createdAt: string
  respondedAt?: string
}

export interface AvailabilityIssuePublic {
  issueId: string
  orderNumber: string
  shopName: string
  shopLogoUrl?: string
  shopCode: string
  status: AvailabilityIssueStatus
  statusDisplay: string
  expiresAt: string
  timeRemaining?: string
  isExpired: boolean
  canRespond: boolean
  operatorMessage?: string
  originalOrderTotal: number
  proposedOrderTotal: number
  difference: number
  currency: string
  items: AvailabilityIssueItemPublic[]
  newLines?: AvailabilityIssueNewLinePublic[]
  /**
   * Full current order lineup — every OrderItem currently on the order,
   * so the customer accept page can render a collapsible "Full order"
   * section alongside "What's changing". `isAffected` marks which rows
   * appear in the affected items list.
   */
  orderItems?: AvailabilityIssueFullOrderItemPublic[]
  createdAt: string
}

export interface AvailabilityIssueFullOrderItemPublic {
  orderItemId: string
  productName: string
  variantName?: string
  productImageUrl?: string
  quantity: number
  unitPrice: number
  lineTotal: number
  isAffected: boolean
  isExtra: boolean
}

export interface AvailabilityIssueNewLinePublic {
  productName: string
  variantName?: string
  productImageUrl?: string
  quantity: number
  unitPrice: number
  lineTotal: number
  notes?: string
}

export interface AvailabilityIssueItemPublic {
  productName: string
  variantName?: string
  productImageUrl?: string
  originalQuantity: number
  originalUnitPrice: number
  originalLineTotal: number
  availableQuantity?: number
  isUnavailable: boolean
  newLineTotal: number
  alternativeProductName?: string
  alternativePrice?: number
  alternativeQuantity?: number
  alternativeNote?: string
  hasAlternative: boolean
  changeDescription: string
}

export interface AvailabilityResponseResult {
  success: boolean
  message: string
  response: string
  orderNumber?: string
  newOrderTotal?: number
  orderUrl?: string
  shopUrl?: string
}

export interface StripeAccountStatus {
  hasAccount: boolean
  accountId?: string
  isComplete?: boolean
  chargesEnabled?: boolean
  payoutsEnabled?: boolean
  detailsSubmitted?: boolean
  canAcceptOrders?: boolean
  message?: string
}

// ============================================================================
// Bulk Import Types
// ============================================================================

export interface ProductImportRow {
  rowNumber: number
  name: string
  variantTitle?: string  // If present, this row is a variant of the product with matching 'name'
  sku?: string
  barcode?: string
  categoryLevel1?: string
  categoryLevel2?: string
  categoryLevel3?: string
  categoryName?: string  // Computed from levels for display
  price: number
  compareAtPrice?: number
  costPrice?: number
  description?: string
  shortDescription?: string
  stockQuantity?: number
  lowStockThreshold?: number
  trackInventory?: boolean
  isFeatured?: boolean
  payableOnArrival?: boolean  // Customer pays on arrival instead of checkout
  unitOfMeasure?: UnitOfMeasure
  weight?: number
  weightUnit?: string
  imageUrls?: string[]
  optionName?: string       // Option group name (e.g., Cut Type)
  optionChoices?: string    // Comma-separated choices (e.g., Curry Cut, Nihari Cut)
  optionPrices?: string     // Comma-separated prices (e.g., 0, 0, 1.00)
  status: 'valid' | 'warning' | 'error'
  errors: string[]
  warnings: string[]
}

export interface ProductImportResult {
  success: boolean
  totalRows: number
  successCount: number
  errorCount: number
  warningCount: number
  errors: ProductImportError[]
  createdProductIds: string[]
}

export interface ProductImportError {
  rowNumber: number
  field: string
  message: string
}

export interface BulkCreateProductRequest {
  products: CreateProductRequest[]
  skipErrors?: boolean
}

// ============================================================================
// Bulk Edit Types
// ============================================================================

export enum BulkPriceUpdateType {
  Set = 0,
  IncreasePercent = 1,
  DecreasePercent = 2
}

export enum BulkStockUpdateType {
  Set = 0,
  Add = 1,
  Remove = 2
}

export interface BulkPriceUpdate {
  type: BulkPriceUpdateType
  value: number
  updateCompareAtPrice?: boolean
  compareAtPriceValue?: number | null
  clearCompareAtPrice?: boolean
}

export interface BulkStockUpdate {
  type: BulkStockUpdateType
  value: number
}

export interface BulkEditProductsRequest {
  productIds: string[]
  priceUpdate?: BulkPriceUpdate
  stockUpdate?: BulkStockUpdate
  categoryId?: string
  isFeatured?: boolean
  // Toggle inventory tracking for the selected products AND every variant
  // under them. Enabling preserves existing stock; disabling nulls stock.
  trackInventory?: boolean
}

export interface BulkEditProductsResponse {
  message: string
  updatedCount: number
  skippedCount: number
  errors: string[]
}

// ============================================================================
// Background Import Types (API-based)
// ============================================================================

export enum ProductImportStatus {
  Pending = 0,
  Processing = 1,
  Completed = 2,
  PartiallyCompleted = 3,
  Failed = 4
}

export interface ProductImportDto {
  id: string
  shopId: string
  shopName: string
  originalFileName: string
  fileSizeBytes: number
  fileSizeDisplay: string
  originalFileUrl: string
  resultFileUrl?: string
  status: ProductImportStatus | string
  statusDisplay: string
  totalRows: number
  processedRows: number
  successCount: number
  errorCount: number
  progressPercent: number
  errorSummary?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
  durationDisplay?: string
}

export interface ProductImportListDto {
  id: string
  originalFileName: string
  fileSizeDisplay: string
  status: ProductImportStatus | string
  statusDisplay: string
  totalRows: number
  successCount: number
  errorCount: number
  createdAt: string
  completedAt?: string
  durationDisplay?: string
}

export interface CreateProductImportResponse {
  importId: string
  message: string
  totalRows: number
}

// ============================================================================
// Product Export Types
// ============================================================================

export enum ProductExportStatus {
  Pending = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3
}

export interface ProductExportDto {
  id: string
  shopId: string
  shopName: string
  requestedByUserId: string
  status: ProductExportStatus | string
  statusDisplay: string
  originalFileName?: string
  fileSizeBytes: number
  fileSizeDisplay?: string
  productCount: number
  variantCount: number
  rowCount: number
  errorMessage?: string
  createdAt: string
  processingStartedAt?: string
  completedAt?: string
  durationDisplay?: string
  // Filter snapshot — null in every slot means "export of everything for the shop".
  filterSearchTerm?: string | null
  filterStatus?: ProductStatus | number | string | null
  filterCategoryId?: string | null
  filterBaseCategoryId?: string | null
  /** Count of products explicitly selected via checkbox on ProductsView; null when none. */
  filterProductIdsCount?: number | null
}

export interface CreateProductExportResponse {
  exportId: string
  message: string
}

export interface ProductExportDownloadResponse {
  downloadUrl: string
  fileName: string
  expiresAt: string
}

// ============================================================================
// Variant + Option Group Library Types
// ============================================================================

/** Single library entry — same shape for shop-scoped and platform-wide entries. */
export interface VariantLibraryEntryDto {
  id: string
  name: string
  defaultPrice?: number
  /** Live count of products in the shop using this name. Always 0 for platform entries. */
  usageCount: number
  /** "Shop" or "Platform" — drives the section the entry shows up under in the picker. */
  source: 'Shop' | 'Platform'
}

export interface OptionGroupLibraryChoiceDto {
  id: string
  name: string
  defaultPriceAdjustment: number
  sortOrder: number
}

export interface OptionGroupLibraryEntryDto {
  id: string
  name: string
  usageCount: number
  source: 'Shop' | 'Platform'
  choices: OptionGroupLibraryChoiceDto[]
}

export interface CreateVariantLibraryEntryRequest {
  name: string
  defaultPrice?: number
}

export interface UpdateVariantLibraryEntryRequest {
  name: string
  defaultPrice?: number
}

export interface OptionGroupLibraryChoiceInput {
  name: string
  defaultPriceAdjustment: number
  sortOrder: number
}

export interface CreateOptionGroupLibraryRequest {
  name: string
  choices: OptionGroupLibraryChoiceInput[]
}

export interface UpdateOptionGroupLibraryRequest {
  name: string
  choices: OptionGroupLibraryChoiceInput[]
}

/** Distinct choice in the library (across all option groups in the shop / platform). */
export interface ChoiceLibraryEntryDto {
  /** Synthetic — name-based — used only as a Vue v-for key. */
  id: string
  name: string
  defaultPriceAdjustment: number
  /** Number of distinct library option groups that contain a choice by this name. */
  usageCount: number
  source: 'Shop' | 'Platform'
}

/** Admin view: a variant name observed across shop libraries. */
export interface AggregatedShopVariantDto {
  name: string
  /** How many shops use a variant with this name. */
  shopCount: number
  /** True if it's already in the platform suggestions. */
  isOnPlatform: boolean
}

/** Admin view: an option-group name observed across shop libraries. */
export interface AggregatedShopOptionGroupDto {
  name: string
  shopCount: number
  isOnPlatform: boolean
  /** Distinct choice names seen across all shops with a group by this name. */
  sampleChoices: string[]
}

// ============================================================================
// Cart Types (for customer storefront)
// ============================================================================

export interface CartItem {
  productId: string
  productVariantId?: string
  product: ProductListItem
  variant?: ProductVariant
  quantity: number
  unitPrice: number
  totalPrice: number
  // Promotional pricing fields
  originalPrice?: number // Original price before promotion (null if no promotion)
  promotionDiscountAmount: number // Total discount for this line item
  promotionName?: string // Name of the applied promotion (e.g., "20% OFF")
  hasDiscount: boolean // True if promotion or compare-at-price discount applied
  discountPercentage?: number // Discount percentage
  displayWasPrice?: number // The "Was" price to display
  // Product option selections
  optionSelections?: OrderItemOptionSelectionDto[]
}

export interface Cart {
  shopId: string
  items: CartItem[]
  subtotal: number
  deliveryFee: number
  total: number
  itemCount: number
}

// ============================================================================
// Shop Gallery Types
// ============================================================================

export interface ShopGalleryImage {
  id: string
  shopId: string
  imageUrl: string
  fileName: string
  altText?: string
  fileSizeBytes: number
  contentType?: string
  width: number
  height: number
  createdAt: string
  /** True when this image is currently the shop's downloadable flyer. */
  isFlyer?: boolean
}

/** Public flyer response shape (both shop + service). */
export interface PublicFlyer {
  imageUrl: string
  fileName: string
  altText?: string
  width?: number
  height?: number
}

export interface UploadGalleryImageRequest {
  altText?: string
}

export interface UpdateGalleryImageRequest {
  altText?: string
}

export interface GalleryFilters {
  page?: number
  pageSize?: number
}

// ============================================================================
// Pagination Types
// ============================================================================

export interface PaginatedResult<T> {
  items: T[]
  totalCount: number
  totalVariantCount?: number
  page: number
  pageSize: number
  totalPages: number
}

// ============================================================================
// Filter Types
// ============================================================================

export interface ProductFilters {
  search?: string
  categoryId?: string
  /** Roll-up filter: returns products under this base category at any depth. */
  baseCategoryId?: string
  status?: ProductStatus
  isFeatured?: boolean
  inStock?: boolean
  lowStock?: boolean
  minPrice?: number
  maxPrice?: number
  page?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
  // Dietary filters used by the public storefront endpoint
  isVegan?: boolean
  isVegetarian?: boolean
  isHalal?: boolean
}

export interface OrderFilters {
  search?: string
  status?: OrderStatus
  fulfillmentType?: FulfillmentType
  /** Channel filter — Website / Mobile App / White Label. */
  source?: OrderSource
  paymentStatus?: 'Pending' | 'PartiallyPaid' | 'Paid'
  hasPendingPayment?: boolean
  fromDate?: string
  toDate?: string
  minTotal?: number
  maxTotal?: number
  page?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

// ============================================================================
// Shop Availability Types (Slot Booking)
// ============================================================================

export enum OrderSlotStatus {
  Confirmed = 'Confirmed',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  NoShow = 'NoShow'
}

/**
 * Rule type for shop availability rules
 */
export enum ShopAvailabilityType {
  Regular = 'Regular',
  Exception = 'Exception',
  Holiday = 'Holiday',
  Vacation = 'Vacation'
}

/**
 * Extended shop availability rule with type and date range
 */
export interface ShopAvailabilityRule {
  id: string
  shopId: string
  dayOfWeek: number
  startTime: string
  endTime: string
  isAvailable: boolean
  capacity: number
  type?: ShopAvailabilityType
  effectiveFrom?: string
  effectiveTo?: string
  notes?: string
}

/**
 * Shop slot booking settings
 */
export interface ShopSlotSettingsDto {
  shopId: string
  offersSlotBooking: boolean
  slotDurationMinutes: number
  minAdvanceBookingMinutes?: number
  maxAdvanceBookingDays?: number
}

export interface UpdateShopSlotSettingsRequest {
  offersSlotBooking: boolean
  slotDurationMinutes?: number
  minAdvanceBookingMinutes?: number
  maxAdvanceBookingDays?: number
}

/**
 * Weekly availability rule for a shop
 */
export interface ShopAvailabilityDto {
  id: string
  shopId: string
  dayOfWeek: number
  startTime: string
  endTime: string
  isAvailable: boolean
  capacity: number
}

/**
 * Public shop opening hours
 */
export interface ShopOpeningHoursDto {
  isOpen: boolean
  openingHoursText: string
  dailyHours: DailyOpeningHoursDto[]
}

export interface DailyOpeningHoursDto {
  dayOfWeek: number
  dayName: string
  isOpen: boolean
  openTime?: string
  closeTime?: string
}

export interface UpdateShopAvailabilityRequest {
  id?: string // null for new, existing ID for update
  dayOfWeek: number
  startTime: string
  endTime: string
  isAvailable: boolean
  capacity?: number
}

export interface UpdateShopAvailabilitiesRequest {
  availabilities: UpdateShopAvailabilityRequest[]
}

/**
 * Date-specific availability override for a shop
 */
export interface ShopDateSpecificAvailabilityDto {
  id: string
  shopId: string
  specificDate: string
  isBlocked: boolean
  capacity?: number
  startTime?: string
  endTime?: string
  notes?: string
  createdAt: string
  updatedAt: string
}

export interface SetShopDateSpecificAvailabilityRequest {
  date: string
  isBlocked?: boolean
  capacity?: number
  startTime?: string
  endTime?: string
  notes?: string
}

/**
 * Order slot information
 */
export interface OrderSlotDto {
  id: string
  orderId: string
  scheduledStartUtc: string
  scheduledEndUtc: string
  status: OrderSlotStatus | string
  customerNotes?: string
  providerNotes?: string
  confirmedAt?: string
  completedAt?: string
  cancelledAt?: string
  cancellationReason?: string
}

export interface RescheduleOrderSlotRequest {
  newStartUtc: string
  reason?: string
}

export interface CancelOrderSlotRequest {
  reason?: string
  processRefund?: boolean
}

/**
 * Time slot for booking
 */
export interface TimeSlotDto {
  startTime: string
  endTime: string
  isAvailable: boolean
  remainingCapacity: number
}

/**
 * Date availability summary for calendar view
 */
export interface ShopDateAvailabilityDto {
  date: string
  isAvailable: boolean
  totalSlots: number
  availableSlots: number
  isBlocked: boolean
  hasOverride: boolean
}

/**
 * Calendar day data (extended for management view)
 */
export interface ShopCalendarDayDto {
  date: string
  dayOfWeek: number
  isWorkingDay: boolean
  baseCapacity: number
  overrideCapacity?: number
  effectiveCapacity: number
  isBlocked: boolean
  hasOverride: boolean
  bookedCount: number
  note?: string
  startTime?: string
  endTime?: string
}

/**
 * Shop calendar response
 */
export interface ShopCalendarResponse {
  shopId: string
  shopName: string
  slotDurationMinutes: number
  weeklyRules: ShopAvailabilityDto[]
  dateSpecificOverrides: ShopDateSpecificAvailabilityDto[]
  calendarDays: ShopCalendarDayDto[]
}

// ============================================================================
// Shop Staff Types
// ============================================================================

/**
 * Shop staff member - a user assigned to manage shop orders
 */
export interface ShopStaff {
  id: string
  userId: string
  shopId: string
  userFullName: string
  userEmail: string
  shopName: string
  assignedAt: string
  assignedByUserId?: string
  assignedByUserName?: string
  isActive: boolean
  notes?: string
  /** Optional internal staff ID assigned by the provider (e.g., employee number) */
  staffId?: string
  createdAt: string
  updatedAt?: string
  /** Whether the user has verified their email (set their password) */
  emailVerified: boolean
  /** Whether the user has ever logged in */
  hasLoggedIn: boolean
}

/**
 * Response containing list of shop staff
 */
export interface ShopStaffListResponse {
  staff: ShopStaff[]
  totalCount: number
}

/**
 * Request to create a new shop staff user
 */
export interface CreateShopStaffRequest {
  email: string
  firstName: string
  surname: string
  phoneNumber?: string
  notes?: string
  /** Optional internal staff ID assigned by the provider (e.g., employee number) */
  staffId?: string
}

/**
 * Response after creating shop staff
 */
export interface CreateShopStaffResponse {
  shopStaff: ShopStaff
  temporaryPassword: string
}

/**
 * Request to assign an existing user as shop staff
 */
export interface AssignShopStaffRequest {
  userId: string
  notes?: string
}

/**
 * Request to update shop staff
 */
export interface UpdateShopStaffRequest {
  isActive?: boolean
  notes?: string
}

/**
 * Shop info for staff users
 */
export interface StaffShopInfo {
  id: string
  name: string
  shopCode: string
  logoUrl?: string
  status: ShopStatus
}

// ============================================================================
// Base Category Types (Shop Category Classification)
// ============================================================================

/**
 * System-defined or provider-created base category for classifying shops.
 * E.g., Grocery, Restaurant, Bakery, Electronics, etc.
 */
export interface BaseCategory {
  id: string
  name: string
  slug: string
  description?: string
  iconName?: string
  imageUrl?: string
  sortOrder: number
  isActive: boolean
  isSystemDefined: boolean
  createdByProviderId?: string
  shopCount: number
  createdAt: string
  updatedAt: string
}

/**
 * Association between a shop and a base category.
 * A shop can belong to multiple base categories.
 */
export interface ShopBaseCategory {
  id: string
  shopId: string
  baseCategoryId: string
  categoryName: string
  categorySlug: string
  categoryIconName?: string
  isPrimary: boolean
  sortOrder: number
  /** Cumulative count of products under categories linked to this base category */
  productCount: number
}

/**
 * Request to set base categories for a shop.
 * Replaces all existing category assignments.
 */
export interface SetShopBaseCategoriesRequest {
  categoryIds: string[]
  primaryCategoryId?: string
}

/**
 * Request to create a custom base category (provider-specific).
 */
export interface CreateBaseCategoryRequest {
  name: string
  description?: string
  iconName?: string
}

// ============================================================================
// Shop Terms & Conditions Types
// ============================================================================

/**
 * Shop terms and conditions data
 */
export interface ShopTermsAndConditions {
  termsAndConditions?: string
  returnPolicy?: string
  refundPolicy?: string
  cancellationPolicy?: string
  requireTermsAcceptance: boolean
  hasTermsAndConditions: boolean
}

/**
 * Request to update shop terms and conditions
 */
export interface UpdateShopTermsRequest {
  termsAndConditions?: string
  returnPolicy?: string
  refundPolicy?: string
  cancellationPolicy?: string
  requireTermsAcceptance: boolean
}

// ============================================================================
// Shop Analytics Types
// ============================================================================

/**
 * Filter parameters for shop analytics queries
 */
export interface ShopAnalyticsFilter {
  startDate: string
  endDate: string
  categoryId?: string
  productId?: string
  groupBy?: 'day' | 'week' | 'month'
  includeComparison?: boolean
}

/**
 * Order statistics for analytics dashboard
 */
export interface ShopOrderStatistics {
  totalOrders: number
  paidOrders: number
  confirmedOrders: number
  processingOrders: number
  completedOrders: number
  cancelledOrders: number
  refundedOrders: number
  partiallyRefundedOrders: number
  pickupOrders: number
  deliveryOrders: number
  totalRevenue: number
  netRevenue: number
  totalRefunds: number
  averageOrderValue: number
  totalDeliveryFees: number
  totalDiscounts: number
  totalItemsSold: number
}

/**
 * Time-period statistic for charts
 */
export interface ShopPeriodStatistic {
  period: string
  periodLabel: string
  orders: number
  completedOrders: number
  cancelledOrders: number
  refundedOrders: number
  pickupOrders: number
  deliveryOrders: number
  revenue: number
  refunds: number
  netRevenue: number
}

/**
 * Period comparison data (current vs previous period)
 */
export interface ShopPeriodComparison {
  currentRevenue: number
  previousRevenue: number
  revenueChangePercent: number
  currentOrders: number
  previousOrders: number
  ordersChangePercent: number
  currentRefunds: number
  previousRefunds: number
  refundsChangePercent: number
  currentNetRevenue: number
  previousNetRevenue: number
  netRevenueChangePercent: number
  currentAverageOrderValue: number
  previousAverageOrderValue: number
  averageOrderValueChangePercent: number
  currentCompletedOrders: number
  previousCompletedOrders: number
  completedOrdersChangePercent: number
}

/**
 * Product performance metrics for analytics
 */
export interface ShopProductPerformance {
  productId: string
  productName: string
  productSku?: string
  categoryId?: string
  categoryName: string
  imageUrl?: string
  quantitySold: number
  totalRevenue: number
  orderCount: number
  averageOrderQuantity: number
  averageUnitPrice: number
}

/**
 * Category performance metrics for analytics
 */
export interface ShopCategoryPerformance {
  categoryId: string
  categoryName: string
  categoryPath: string  // Full path e.g. "Meat > Poultry > Chicken"
  productCount: number
  quantitySold: number
  totalRevenue: number
  orderCount: number
  percentageOfTotalRevenue: number
}

/**
 * Refund summary for recent refunds list
 */
export interface ShopRefundSummary {
  orderId: string
  orderNumber: string
  refundedAt: string
  amount: number
  reason?: string
  customerName: string
  customerEmail: string
}

/**
 * Top customer details
 */
export interface TopCustomer {
  customerEmail: string
  customerName: string
  orderCount: number
  totalSpent: number
  firstOrderDate?: string
  lastOrderDate?: string
}

/**
 * Customer statistics for analytics dashboard
 */
export interface ShopCustomerStatistics {
  totalUniqueCustomers: number
  newCustomers: number
  returningCustomers: number
  newCustomerRevenue: number
  returningCustomerRevenue: number
  averageOrdersPerCustomer: number
  averageRevenuePerCustomer: number
  topCustomers: TopCustomer[]
}

/**
 * Full shop analytics report
 */
export interface ShopAnalyticsReport {
  shopId: string
  shopName: string
  statistics: ShopOrderStatistics
  comparison?: ShopPeriodComparison
  groupedStats: ShopPeriodStatistic[]
  customerStatistics: ShopCustomerStatistics
  topProducts: ShopProductPerformance[]
  fastestSellingProducts: ShopProductPerformance[]
  topCategories: ShopCategoryPerformance[]
  recentRefunds: ShopRefundSummary[]
  startDate: string
  endDate: string
  groupBy: string
}

// ============================================================================
// Custom Domain Types
// ============================================================================

export enum CustomDomainStatus {
  None = 'None',
  Pending = 'Pending',
  DnsVerified = 'DnsVerified',
  Active = 'Active',
  Failed = 'Failed',
  Suspended = 'Suspended'
}

export enum SslCertificateStatus {
  None = 'None',
  Provisioning = 'Provisioning',
  Active = 'Active',
  RenewalPending = 'RenewalPending',
  Expired = 'Expired',
  Failed = 'Failed'
}

/**
 * Status of Apple Pay domain registration with Stripe for the shop's
 * white-label custom domain. Only flips to `Active` after Stripe verifies
 * the domain via the .well-known file.
 */
export enum ApplePayDomainStatus {
  None = 'None',
  Pending = 'Pending',
  Active = 'Active',
  Failed = 'Failed'
}

/**
 * Custom domain configuration for a shop
 */
export interface CustomDomainConfig {
  shopId: string
  shopName: string
  shopCode: string
  // Custom Domain
  customDomain?: string
  customDomainStatus: CustomDomainStatus
  customDomainAddedAt?: string
  customDomainVerifiedAt?: string
  customDomainError?: string
  // SSL Certificate (Legacy - Let's Encrypt)
  sslStatus: SslCertificateStatus
  sslCertificateIssuedAt?: string
  sslCertificateExpiresAt?: string
  sslCertificateError?: string
  // AWS ACM Certificate
  acmStatus?: AcmCertificateStatus
  acmValidationCname?: string
  acmValidationValue?: string
  acmRequestedAt?: string
  acmIssuedAt?: string
  acmError?: string
  // Apple Pay domain registration (Stripe payment_method_domains)
  applePayDomainStatus?: ApplePayDomainStatus
  applePayRegisteredAt?: string
  applePayError?: string
  // White-Label Options
  hideZaveatoBranding: boolean
  redirectToCustomDomain: boolean
  // Zaveato Subdomain
  zaveatoSubdomain?: string
  zaveatoSubdomainActive: boolean
  // Display helpers
  fullSubdomainUrl?: string
  fullCustomDomainUrl?: string
}

/**
 * AWS ACM certificate status
 */
export type AcmCertificateStatus = 'None' | 'PendingValidation' | 'Issued' | 'ValidationFailed' | 'Expired'

/**
 * DNS verification result
 */
export interface DomainVerificationResult {
  success: boolean
  recordType?: string
  expectedValue?: string
  resolvedTo?: string
  error?: string
  checkedAt?: string
}

/**
 * DNS setup instructions for custom domain
 */
export interface DnsInstructions {
  domain: string
  // Step 1: Point domain to Zaveato
  recordType: string
  recordName: string
  recordValue: string
  ttl?: number
  alternativeRecordType?: string
  alternativeRecordValue?: string
  // Step 2: SSL Certificate validation (ACM)
  sslValidationRecordName?: string
  sslValidationRecordValue?: string
  // Additional guidance
  instructions?: string[]
  commonMistakes?: string[]
  estimatedPropagationTime?: string
}

/**
 * Domain verification log entry
 */
export interface DomainVerificationLog {
  id: string
  domain: string
  checkedAt: string
  dnsResolved: boolean
  resolvedTo?: string
  expectedValue?: string
  recordType?: string
  success: boolean
  errorMessage?: string
  // Keep for backwards compatibility
  status?: string
  verificationType?: string
}

/**
 * Request to add a custom domain
 */
export interface AddCustomDomainRequest {
  domain: string
}

/**
 * Request to claim a Zaveato subdomain
 */
export interface ClaimSubdomainRequest {
  subdomain: string
}

/**
 * Request to update white-label settings
 */
export interface UpdateWhiteLabelRequest {
  hideZaveatoBranding: boolean
  redirectToCustomDomain: boolean
}

/**
 * Domain availability check result
 */
export interface DomainAvailabilityResult {
  domain?: string
  subdomain?: string
  available: boolean
}

// ============================================================================
// Shop Promo Code Types
// ============================================================================

export enum ShopPromoCodeType {
  PercentageOff = 'PercentageOff',
  FixedAmountOff = 'FixedAmountOff',
  FreeDelivery = 'FreeDelivery'
}

export enum ShopPromoCodeScope {
  ShopWide = 'ShopWide',
  SelectedCategories = 'SelectedCategories'
}

/**
 * Lightweight category info for promo code
 */
export interface PromoCodeCategory {
  categoryId: string
  categoryName: string
}

/**
 * Shop promo code
 */
export interface ShopPromoCode {
  id: string
  shopId: string
  code: string
  name: string
  description?: string
  type: ShopPromoCodeType
  typeDisplay: string
  discountValue: number
  maxDiscountAmount?: number
  scope: ShopPromoCodeScope
  scopeDisplay: string
  validFrom?: string
  validTo?: string
  minimumOrderAmount?: number
  maxUses?: number
  maxUsesPerCustomer?: number
  isActive: boolean
  isCurrentlyValid: boolean
  usageCount: number
  totalDiscountGiven: number
  categories: PromoCodeCategory[]
  createdAt: string
  updatedAt?: string
}

/**
 * Summary of shop promo codes
 */
export interface ShopPromoCodesSummary {
  totalPromoCodes: number
  activePromoCodes: number
  expiredPromoCodes: number
  totalUsages: number
  totalDiscountGiven: number
  promoCodes: ShopPromoCode[]
}

/**
 * Public promo code with shop info for display on homepage/deals page
 */
export interface PublicPromoCode {
  id: string
  code: string
  name: string
  description?: string
  type: ShopPromoCodeType
  typeDisplay: string
  discountValue: number
  maxDiscountAmount?: number
  minimumOrderAmount?: number
  validFrom?: string
  validTo?: string
  // Shop info
  shopId: string
  shopName: string
  shopCode: string
  shopLogoUrl?: string
  shopCategoryName?: string
  /** ISO 4217 currency code of the shop that owns the promo. */
  shopCurrency?: string
}

/**
 * Response for public promo codes listing
 */
export interface PublicPromoCodesResponse {
  promoCodes: PublicPromoCode[]
  totalCount: number
}

/**
 * Request to create a promo code
 */
export interface CreateShopPromoCodeRequest {
  twoFactorCode?: string
  code: string
  name: string
  description?: string
  type: ShopPromoCodeType
  discountValue: number
  maxDiscountAmount?: number
  scope: ShopPromoCodeScope
  categoryIds?: string[]
  validFrom?: string
  validTo?: string
  minimumOrderAmount?: number
  maxUses?: number
  maxUsesPerCustomer?: number
  isActive?: boolean
}

/**
 * Request to update a promo code
 */
export interface UpdateShopPromoCodeRequest {
  twoFactorCode?: string
  code?: string
  name?: string
  description?: string
  type?: ShopPromoCodeType
  discountValue?: number
  maxDiscountAmount?: number
  scope?: ShopPromoCodeScope
  categoryIds?: string[]
  validFrom?: string
  validTo?: string
  clearValidFrom?: boolean
  clearValidTo?: boolean
  minimumOrderAmount?: number
  clearMinimumOrderAmount?: boolean
  maxUses?: number
  clearMaxUses?: boolean
  maxUsesPerCustomer?: number
  clearMaxUsesPerCustomer?: boolean
}

/**
 * Request for secure promo code operations (activate/deactivate/delete)
 */
export interface PromoCodeSecureOperationRequest {
  twoFactorCode?: string
}

/**
 * Request to validate a promo code at checkout
 */
export interface ValidatePromoCodeRequest {
  shopId: string
  code: string
  subtotal: number
  deliveryFee: number
  basketCategoryIds?: string[]
  customerId?: string
  customerEmail?: string
}

/**
 * Response from promo code validation
 */
export interface ValidatePromoCodeResponse {
  isValid: boolean
  errorMessage?: string
  errorCode?: string
  promoCodeId?: string
  code?: string
  type?: ShopPromoCodeType
  discountAmount: number
  discountDescription?: string
  newSubtotal: number
  newDeliveryFee: number
  newTotal: number
}

/**
 * Promo code usage record
 */
export interface PromoCodeUsage {
  orderId: string
  orderNumber?: string
  customerName: string
  customerEmail: string
  orderTotal: number
  discountAmount: number
  usedAt: string
}

/**
 * Promo code analytics
 */
export interface PromoCodeAnalytics {
  promoCodeId: string
  code: string
  name: string
  totalUses: number
  totalDiscountGiven: number
  averageDiscountPerUse: number
  totalOrderValue: number
  averageOrderValue: number
  lastUsedAt?: string
  recentUsages: PromoCodeUsage[]
}

/**
 * Shop document / certificate
 */
export interface ShopDocument {
  id: string
  shopId: string
  title: string
  documentUrl: string
  fileName: string
  fileSize: number
  contentType: string
  isPublic: boolean
  createdAt?: string
}
